"use client";

/**
 * Блок «Темы поездок» — горизонтальная карусель с боковыми круглыми стрелками
 * (как у остальных каруселей сайта), карточки одинаковой высоты. Данные — из
 * настроек (rent_site_global.data.themes). Карточка ведёт на свою ссылку (link),
 * по умолчанию — в каталог /rent.
 */

import Image from "next/image";
import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";
import CarouselArrow from "./CarouselArrow";
import type { ThemeItem } from "@/lib/fleet-server";
import { useLocale } from "@/lib/locale-context";

const FALLBACK = "/images/catalog-car.png";

export default function TravelThemes({ items = [] }: { items?: ThemeItem[] }) {
  const { t: tr, locale, href } = useLocale();
  const pick = (a?: string, b?: string) =>
    locale === "en"
      ? (b && b.trim() ? b : a && a.trim() ? a : "")
      : (a && a.trim() ? a : b && b.trim() ? b : "");
  const ref = useRef<HTMLDivElement | null>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 1);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);
  useEffect(() => {
    update();
    const el = ref.current;
    el?.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => { el?.removeEventListener("scroll", update); window.removeEventListener("resize", update); };
  }, [update, items.length]);

  if (!items.length) return null;

  const scrollBy = (d: number) => ref.current?.scrollBy({ left: d, behavior: "smooth" });

  return (
    <div className="relative">
      <div
        ref={ref}
        className="flex snap-x items-stretch gap-6 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {items.map((t) => {
          const title = pick(t.title_ru, t.title_en);
          const subtitle = pick(t.subtitle_ru, t.subtitle_en);
          return (
            <div key={t.slug} className="w-[300px] shrink-0 snap-start sm:w-[360px]">
              <Link
                href={href(`/themes/${t.slug}`)}
                className="group flex h-full flex-col overflow-hidden rounded-2xl border border-black/[0.08] bg-[#f6faff] transition hover:shadow-[var(--shadow-card)]"
              >
                <div className="relative h-[200px] shrink-0 overflow-hidden sm:h-[220px]">
                  <Image src={t.image || FALLBACK} alt={title} fill sizes="(max-width: 768px) 100vw, 360px" className="object-cover transition-transform duration-500 group-hover:scale-[1.04]" />
                </div>
                <div className="flex flex-1 flex-col justify-between gap-5 p-5 sm:p-6">
                  <div className="flex flex-col gap-3">
                    <h3 className="line-clamp-2 text-[18px] font-semibold leading-[1.25] text-black transition group-hover:text-primary sm:text-[22px] lg:text-[24px]">{title}</h3>
                    {subtitle ? <p className="line-clamp-2 text-[14px] leading-6 text-text-muted sm:text-[16px]">{subtitle}</p> : null}
                  </div>
                  {t.price_from ? (
                    <div className="flex items-end gap-2 font-semibold">
                      <span className="text-[18px] text-text-muted sm:text-[22px]">{tr("price.from")}</span>
                      <span className="text-[24px] leading-none text-black sm:text-[30px]">{t.price_from}</span>
                      <span className="text-[18px] text-text-muted sm:text-[22px]">{tr("price.perDay")}</span>
                    </div>
                  ) : null}
                </div>
              </Link>
            </div>
          );
        })}
      </div>

      {canLeft && (
        <CarouselArrow direction="left" ariaLabel={tr("a11y.prev")} onClick={() => scrollBy(-380)}
          className="!rounded-full absolute left-0 top-1/2 z-10 -translate-y-1/2 shadow-[0_8px_24px_rgba(20,29,69,0.18)]" />
      )}
      {canRight && (
        <CarouselArrow direction="right" ariaLabel={tr("a11y.next")} onClick={() => scrollBy(380)}
          className="!rounded-full absolute right-0 top-1/2 z-10 -translate-y-1/2 shadow-[0_8px_24px_rgba(20,29,69,0.18)]" />
      )}
    </div>
  );
}
